import React, { useContext } from 'react';
import _get from 'lodash/get';
import { string, arrayOf, oneOfType, func, objectOf, shape } from 'prop-types';
import { TtpContext, formatTtp } from '../../utils/ttp';

/**
 * Provides React context for TTP messages to children via Context API.
 * @extends {Component<Props>}
 * @example
 * const translations = { foo: "bar" }
 * <TtpProvider value={translations}>
 *   <App />
 * </TtpProvider>
 */
const TtpProvider = (props) => <TtpContext.Provider {...props} />;
TtpProvider.propTypes = {
  /** Object that should contain TTP messages for current locale */
  value: shape({}),
};
TtpProvider.defaultProps = {
  value: {},
};

/**
 * Retrieves and renders one or more TTP messages from TtpContext.
 * @extends {Component<Props>}
 * @example
 * // Given context:
 * // {
 * //   foo: {
 * //     a: Hello World,
 * //     b: This is ##%boldText##bold##%##,
 * //     c: Value: ##value##
 * //   }
 * // }
 *
 * // Hello World
 * <TtpContent id="foo.a" />
 *
 * // This is <b>bold</b>
 * <TtpContent id="foo.b" substrings={{boldText: text => <b>{text}</b>}} />
 *
 * // <button aria-label="Value: 1">Hello World</button>
 * <TtpContent id={["foo.a", "foo.c"]} substrings={{value: 1}}>
 *   {(a,c) => <button aria-label={c}>{a}</button>}
 * </TtpContent>
 */
const TtpContent = ({ id, children, substrings }) => {
  const context = useContext(TtpContext);
  const message = Array.isArray(id) ? id.map((x) => _get(context, x)) : _get(context, id);

  // if user passed in array of ids
  if (Array.isArray(message)) {
    const formattedMessages = message.map((msg, idx) => {
      if (Array.isArray(substrings)) {
        return formatTtp(msg, substrings[idx]);
      }
      return formatTtp(msg, substrings);
    });
    if (typeof children === 'function') {
      return children(...formattedMessages);
    }
    return formattedMessages.join(' ');
  }
  // else
  let formattedMessage;
  if (Array.isArray(substrings)) {
    formattedMessage = formatTtp(message, substrings[0]);
  } else {
    formattedMessage = formatTtp(message, substrings);
  }

  if (typeof children === 'function') {
    return children(formattedMessage);
  }
  return formattedMessage;
};
TtpContent.propTypes = {
  /** Path of message(s) to be retrieved from TtpContext object.
   * Can accept multiple ids as an array in necessary
   */
  id: oneOfType([string, arrayOf(string)]).isRequired,
  /** React compositional child.
   * Expects a function that accepts substrings and returns a component */
  children: func,
  /** Object properties are used for substitution.
   * Values are substituted into message based on key.
   * Can accept array of objects in case multiple ids exist */
  substrings: oneOfType([
    objectOf(oneOfType([string, func])),
    arrayOf(objectOf(oneOfType([string, func]))),
  ]),
};
TtpContent.defaultProps = {
  children: null,
  substrings: {},
};

export { TtpProvider };
export default TtpContent;
